import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'

interface PedagogicalNotesProps {
  entityType: 'student' | 'unit' | 'membership'
  entityId: number
  onClose?: () => void
}

export default function PedagogicalNotes({
  entityType,
  entityId,
  onClose
}: PedagogicalNotesProps) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingNote, setEditingNote] = useState<any>(null)
  const [formData, setFormData] = useState({
    note_type: 'general',
    content: '',
    visibility_restriction: 'class_teacher'
  })

  const queryClient = useQueryClient()

  // Fetch notes
  const { data: notesData, isLoading } = useQuery(
    ['pedagogical-notes', entityType, entityId],
    () =>
      api
        .get('/pedagogical-notes', {
          params: { entity_type: entityType, entity_id: entityId }
        })
        .then(res => res.data)
  )

  const notes = notesData?.data || []

  // Create mutation
  const createMutation = useMutation(
    (data: any) => api.post('/pedagogical-notes', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pedagogical-notes', entityType, entityId])
        setShowAddModal(false)
        setFormData({
          note_type: 'general',
          content: '',
          visibility_restriction: 'class_teacher'
        })
        toast.success('Notiz erfolgreich erstellt')
      },
      onError: (error: any) => {
        console.error('Error creating note:', error)
        toast.error('Fehler beim Erstellen der Notiz')
      }
    }
  )

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) =>
      api.put(`/pedagogical-notes/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pedagogical-notes', entityType, entityId])
        setEditingNote(null)
        toast.success('Notiz erfolgreich aktualisiert')
      },
      onError: (error: any) => {
        console.error('Error updating note:', error)
        toast.error('Fehler beim Aktualisieren der Notiz')
      }
    }
  )

  // Delete mutation
  const deleteMutation = useMutation(
    (id: number) => api.delete(`/pedagogical-notes/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pedagogical-notes', entityType, entityId])
        toast.success('Notiz erfolgreich gelöscht')
      },
      onError: (error: any) => {
        console.error('Error deleting note:', error)
        toast.error('Fehler beim Löschen der Notiz')
      }
    }
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const data = {
      entity_type: entityType,
      entity_id: entityId,
      ...formData
    }

    if (editingNote) {
      updateMutation.mutate({ id: editingNote.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleDelete = (id: number) => {
    if (confirm('Möchten Sie diese Notiz wirklich löschen?')) {
      deleteMutation.mutate(id)
    }
  }

  const getNoteTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      general: 'Allgemein',
      behavior: 'Verhalten',
      learning: 'Lernen',
      social: 'Sozial'
    }
    return labels[type] || type
  }

  const getVisibilityLabel = (visibility: string) => {
    const labels: Record<string, string> = {
      all: 'Alle',
      class_teacher: 'Klassenlehrer',
      subject_teacher: 'Fachlehrer',
      admin: 'Administratoren'
    }
    return labels[visibility] || visibility
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Pädagogische Notizen</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditingNote(null)
              setShowAddModal(true)
            }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            Neue Notiz
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>Keine pädagogischen Notizen vorhanden</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note: any) => (
            <div
              key={note.id}
              className="border border-gray-200 rounded-lg p-4 space-y-2"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      {getNoteTypeLabel(note.note_type)}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded flex items-center gap-1">
                      {note.visibility_restriction === 'all' ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <EyeOff className="h-3 w-3" />
                      )}
                      {getVisibilityLabel(note.visibility_restriction)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(note.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {note.content}
                  </p>
                  {note.username && (
                    <p className="text-xs text-gray-500 mt-2">
                      von {note.first_name} {note.last_name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingNote(note)
                      setFormData({
                        note_type: note.note_type,
                        content: note.content,
                        visibility_restriction: note.visibility_restriction
                      })
                      setShowAddModal(true)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h4 className="text-lg font-semibold mb-4">
              {editingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}
            </h4>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notiztyp
                </label>
                <select
                  value={formData.note_type}
                  onChange={e =>
                    setFormData({ ...formData, note_type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="general">Allgemein</option>
                  <option value="behavior">Verhalten</option>
                  <option value="learning">Lernen</option>
                  <option value="social">Sozial</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inhalt *
                </label>
                <textarea
                  value={formData.content}
                  onChange={e =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sichtbarkeit
                </label>
                <select
                  value={formData.visibility_restriction}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      visibility_restriction: e.target.value
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="all">Alle</option>
                  <option value="class_teacher">Klassenlehrer</option>
                  <option value="subject_teacher">Fachlehrer</option>
                  <option value="admin">Administratoren</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false)
                    setEditingNote(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50"
                >
                  {editingNote ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}





